import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FaUserShield, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff,
  FaSearch,
} from 'react-icons/fa';
import { adminAccountsService } from '../../services/adminAccountsService';
import { useAuth } from '../../context/AuthContext';
import ModalAdminUser from './components/ModalAdminUser';
import PageHeader from '../../components/PageHeader';
import Skeleton from '../../components/Skeleton';
import { toastSuccess, toastError } from '../../utils/toast';
import { swalConfirm } from '../../utils/swal';

const Badge = ({ active }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
    style={active
      ? { backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.25)' }
      : { backgroundColor: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.2)' }
    }
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: active ? '#22C55E' : '#F87171' }} />
    {active ? 'Activo' : 'Inactivo'}
  </span>
);

const AdminCuentasPage = () => {
  const { currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, editData: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAccountsService.getAll();
      setAccounts(data);
      setFiltered(data);
    } catch (err) {
      toastError('Error al cargar administradores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      q ? accounts.filter(a =>
        a.full_name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q)
      ) : accounts
    );
  }, [search, accounts]);

  const openCreate = () => setModal({ open: true, editData: null });
  const openEdit   = (row) => setModal({ open: true, editData: row });
  const closeModal = () => setModal({ open: false, editData: null });

  const handleSave = async (form, editData) => {
    try {
      if (editData) {
        await adminAccountsService.update(editData.id, {
          full_name: form.full_name,
          email: form.email,
          auth_user_id: editData.auth_user_id,
        });
        // Reset password solo si se llenó
        if (form.password) {
          await adminAccountsService.resetPassword(editData.auth_user_id, form.password);
        }
        toastSuccess('Administrador actualizado');
      } else {
        await adminAccountsService.create({
          full_name: form.full_name,
          email: form.email,
          password: form.password,
        });
        toastSuccess('Administrador creado');
      }
      load();
    } catch (err) {
      toastError(err.message || 'Error al guardar');
      throw err; // re-throw para que modal no cierre
    }
  };

  const handleToggleActive = async (row) => {
    const next = !row.is_active;
    const confirmed = await swalConfirm(
      next ? '¿Activar administrador?' : '¿Desactivar administrador?',
      next
        ? `${row.full_name} podrá acceder al panel nuevamente.`
        : `${row.full_name} no podrá iniciar sesión.`
    );
    if (!confirmed) return;
    try {
      await adminAccountsService.toggleActive(row.id, next);
      toastSuccess(next ? 'Administrador activado' : 'Administrador desactivado');
      load();
    } catch {
      toastError('Error al cambiar estado');
    }
  };

  const handleDelete = async (row) => {
    if (row.auth_user_id === currentUser?.id) {
      toastError('No puedes eliminar tu propia cuenta');
      return;
    }
    const confirmed = await swalConfirm(
      '¿Eliminar administrador?',
      `Esta acción eliminará permanentemente la cuenta de ${row.full_name}.`
    );
    if (!confirmed) return;
    try {
      await adminAccountsService.delete(row.id, row.auth_user_id);
      toastSuccess('Administrador eliminado');
      load();
    } catch {
      toastError('Error al eliminar');
    }
  };

  return (
    <div>
      <PageHeader title="Administradores" subtitle="Gestiona cuentas de acceso al panel">
        <motion.button
          type="button"
          whileTap={{ scale: 0.97 }}
          onClick={openCreate}
          className="flex items-center gap-2 px-4 h-9 rounded-xl text-xs font-semibold transition-colors"
          style={{ background: 'linear-gradient(135deg,#E5252C,#B8161D)', color: '#fff' }}
        >
          <FaPlus size={10} /> Nuevo administrador
        </motion.button>
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16,1,0.3,1] }}
        className="space-y-4"
        style={{
          background: 'rgb(var(--panel))',
          border: '1px solid rgb(var(--fg) / 0.07)',
          boxShadow: 'var(--shadow)',
          borderRadius: '24px',
          padding: '20px',
        }}
      >
        {/* Buscar */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 h-9 rounded-xl" style={{ backgroundColor: 'rgb(var(--raised))', border: '1px solid rgb(var(--fg) / 0.07)', transition: 'border-color .2s' }}
            onFocus={e => e.currentTarget.style.borderColor = 'rgba(0,194,255,0.4)'}
            onBlur={e => e.currentTarget.style.borderColor = 'rgb(var(--fg) / 0.07)'}
          >
            <FaSearch size={11} style={{ color: 'rgb(var(--fg-faint))' }} />
            <input
              type="text"
              placeholder="Buscar por nombre o correo…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-transparent outline-none text-xs w-52"
              style={{ color: 'rgb(var(--fg))', caretColor: '#00C2FF' }}
            />
          </div>
          <span className="text-xs" style={{ color: 'rgb(var(--fg-faint))' }}>
            {filtered.length} administrador{filtered.length !== 1 ? 'es' : ''}
          </span>
        </div>

        {/* Tabla */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgb(var(--fg) / 0.06)' }}>
        {loading ? (
          <Skeleton.TableRows rows={5} cols={6} />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FaUserShield size={28} style={{ color: 'rgb(var(--fg-faint))' }} />
            <p className="text-sm" style={{ color: 'rgb(var(--fg-faint))' }}>
              {search ? 'Sin resultados para esa búsqueda' : 'Sin administradores registrados'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgb(var(--fg) / 0.05)' }}>
                {['Nombre', 'Correo', 'Rol', 'Estado', 'Creado', ''].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: 'rgb(var(--fg-faint))' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: i < filtered.length - 1 ? '1px solid rgb(var(--fg) / 0.03)' : 'none',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgb(var(--fg) / 0.015)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 select-none"
                        style={{ backgroundColor: '#DC1F26' }}
                      >
                        {row.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="text-sm font-medium" style={{ color: 'rgb(var(--fg))' }}>
                        {row.full_name}
                        {row.auth_user_id === currentUser?.id && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: 'rgba(59,130,246,0.12)', color: '#60A5FA' }}>
                            Tú
                          </span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm" style={{ color: 'rgb(var(--fg-dim))' }}>{row.email}</td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: 'rgba(59,130,246,0.1)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.2)' }}>
                      {row.role || 'admin'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5"><Badge active={row.is_active} /></td>
                  <td className="px-5 py-3.5 text-xs" style={{ color: 'rgb(var(--fg-faint))' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <ActionBtn
                        title="Editar"
                        color="#3B82F6"
                        onClick={() => openEdit(row)}
                      >
                        <FaEdit size={12} />
                      </ActionBtn>
                      <ActionBtn
                        title={row.is_active ? 'Desactivar' : 'Activar'}
                        color={row.is_active ? '#FBBF24' : '#4ADE80'}
                        onClick={() => handleToggleActive(row)}
                      >
                        {row.is_active ? <FaToggleOn size={14} /> : <FaToggleOff size={14} />}
                      </ActionBtn>
                      {row.auth_user_id !== currentUser?.id && (
                        <ActionBtn
                          title="Eliminar"
                          color="#F87171"
                          onClick={() => handleDelete(row)}
                        >
                          <FaTrash size={11} />
                        </ActionBtn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </motion.div>

      <ModalAdminUser
        open={modal.open}
        editData={modal.editData}
        onClose={closeModal}
        onSave={handleSave}
      />
    </div>
  );
};

const ActionBtn = ({ children, onClick, title, color }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className="h-7 w-7 rounded-lg flex items-center justify-center transition-all"
    style={{ color, backgroundColor: color + '15' }}
    onMouseEnter={e => e.currentTarget.style.backgroundColor = color + '28'}
    onMouseLeave={e => e.currentTarget.style.backgroundColor = color + '15'}
  >
    {children}
  </button>
);

export default AdminCuentasPage;
